import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { useSchoolId } from '../lib/auth/schoolContext';
import AppLayout from '../components/AppLayout';
import { db } from '../firebase';

export default function StudentInterventionHistory() {
  const { id } = useParams();
  const { schoolId } = useSchoolId();
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', type: '', range: 'all' });

  useEffect(() => {
    (async () => {
      setLoading(true);
      const snap = await getDocs(query(
        collection(db, 'interventions'),
        where('schoolId', '==', schoolId),
        where('students', 'array-contains', id)
      ));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setInterventions(list);
      setLoading(false);
    })();
  }, [id, schoolId]);

  const uniqueTypes = useMemo(() => [...new Set(interventions.map(i => i.type).filter(Boolean))], [interventions]);

  const inRange = (i) => {
    if (filters.range === 'all') return true;
    const now = new Date();
    const msInWeek = 7 * 24 * 60 * 60 * 1000;
    const weeks = filters.range === '12w' ? 12 : filters.range === '24w' ? 24 : 52;
    const pick = i.endDate || i.startDate || i.createdAt;
    let when = 0;
    try {
      if (pick && typeof pick.toDate === 'function') when = pick.toDate().getTime();
      else if (pick instanceof Date) when = pick.getTime();
      else if (typeof pick === 'string') when = new Date(pick).getTime();
    } catch {}
    if (!when || isNaN(when)) return true;
    return (now.getTime() - when) / msInWeek <= weeks;
  };

  const filtered = useMemo(() => {
    return interventions.filter(i => {
      if (filters.status !== 'all' && (i.status || 'active') !== filters.status) return false;
      if (filters.type && i.type !== filters.type) return false;
      if (!inRange(i)) return false;
      return true;
    });
  }, [interventions, filters]);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Intervention History</h1>
            <p className="text-sm text-gray-600">Full history with filters for status, type, and time range.</p>
          </div>
          <Link to={`/student/${id}`} className="px-3 py-2 border rounded hover:bg-gray-50">Back to Profile</Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded shadow p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Status</label>
            <select className="w-full border rounded p-2" value={filters.status} onChange={(e)=> setFilters(f => ({...f, status: e.target.value}))}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Type</label>
            <select className="w-full border rounded p-2" value={filters.type} onChange={(e)=> setFilters(f => ({...f, type: e.target.value}))}>
              <option value="">All</option>
              {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Time Range</label>
            <select className="w-full border rounded p-2" value={filters.range} onChange={(e)=> setFilters(f => ({...f, range: e.target.value}))}>
              <option value="all">All</option>
              <option value="12w">Last 12 weeks</option>
              <option value="24w">Last 24 weeks</option>
              <option value="52w">Last academic year (~52w)</option>
            </select>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded shadow overflow-hidden">
          <div className="px-4 py-3 border-b">
            <div className="text-sm text-gray-700">Showing {filtered.length} of {interventions.length}</div>
          </div>
          {loading ? (
            <div className="p-6 text-gray-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-gray-500">No interventions match your filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Staff</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map(i => (
                    <tr key={i.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">{i.name || 'Intervention'}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{i.type || '-'}</td>
                      <td className="px-6 py-3 text-sm">
                        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{i.status || 'active'}</span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700">{i.leadStaffName || '-'}</td>
                      <td className="px-6 py-3 text-sm text-gray-500">{i.startDate || '-'} → {i.endDate || (i.status === 'active' ? 'ongoing' : '-')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
